import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_FORAGE_CONFIG, INITIAL_FORAGE_SNAPSHOT } from './forageConfig';

function useForageSimulation() {
    const [config, setConfig] = useState(DEFAULT_FORAGE_CONFIG);
    const [snapshot, setSnapshot] = useState(INITIAL_FORAGE_SNAPSHOT);
    const [paused, setPaused] = useState(false);
    const [mode, setModeState] = useState('learn');
    const [evaluationSpeed, setEvaluationSpeedState] = useState(1);
    const workerRef = useRef(null);

    useEffect(() => {
        if (typeof Worker === 'undefined') return undefined;

        const worker = new Worker('/workers/forageWorker.js');
        workerRef.current = worker;
        worker.onmessage = ({ data }) => {
            if (data.type === 'snapshot') {
                setSnapshot(data.snapshot);
                setPaused(data.snapshot.paused);
            }
        };
        worker.postMessage({ type: 'configure', config });

        return () => {
            worker.terminate();
            workerRef.current = null;
        };
        // The worker should be created once; configuration changes use the effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        workerRef.current?.postMessage({ type: 'configure', config });
    }, [config]);

    const updateConfig = useCallback((key, value) => {
        setConfig((current) => ({
            ...current,
            [key]: key === 'brain' ? value : Number(value),
        }));
    }, []);

    const togglePaused = useCallback(() => {
        setPaused((current) => {
            workerRef.current?.postMessage({ type: 'pause', paused: !current });
            return !current;
        });
    }, []);

    const setMode = useCallback((nextMode) => {
        setModeState(nextMode);
        setPaused(false);
        workerRef.current?.postMessage({ type: 'setMode', mode: nextMode });
    }, []);

    const updateEvaluationSpeed = useCallback((speed) => {
        const numericSpeed = Number(speed);
        setEvaluationSpeedState(numericSpeed);
        workerRef.current?.postMessage({ type: 'evaluationSpeed', speed: numericSpeed });
    }, []);

    const restartEvaluation = useCallback(() => {
        setPaused(false);
        workerRef.current?.postMessage({ type: 'restartEvaluation' });
    }, []);

    const reset = useCallback(() => {
        workerRef.current?.postMessage({ type: 'reset' });
    }, []);

    const newWorld = useCallback(() => {
        setConfig((current) => ({ ...current, seed: current.seed + 1 }));
    }, []);

    return {
        config,
        snapshot,
        paused,
        mode,
        evaluationSpeed,
        updateConfig,
        togglePaused,
        setMode,
        updateEvaluationSpeed,
        restartEvaluation,
        reset,
        newWorld,
    };
}

export default useForageSimulation;
